import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import logger from '../utils/logger';
import { fetchCompany, fetchPriceHistory, fetchSignals, fetchConsensusHistory, fetchConsensusValuation } from '../utils/api';
import { useStockChart } from '../hooks/useStockChart';
import LoadingState from './LoadingState';
import ErrorState from './ErrorState';
import ConsensusDisplay from './ConsensusDisplay';

function StockDetail() {
  const { ticker } = useParams();
  const [stock, setStock] = useState(null);
  const [priceHistory, setPriceHistory] = useState(null);
  const [signals, setSignals] = useState(null);
  const [consensusHistory, setConsensusHistory] = useState(null);
  const [selectedConsensusMonth, setSelectedConsensusMonth] = useState(null); // null = latest
  const [consensusValuation, setConsensusValuation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { chartContainerRef } = useStockChart(priceHistory);

  useEffect(() => {
    let isCancelled = false;
    const abortController = new AbortController();

    // Fetch stock details
    const fetchStockDetails = async () => {
      logger.log('Fetching stock details for:', ticker);
      // Use fetchCompany from api.js to respect REACT_APP_API_URL environment variable
      const stockData = await fetchCompany(ticker);
      if (!isCancelled) {
        logger.log('Stock data received:', stockData);
        setStock(stockData);
      }
      return stockData;
    };

    // Fetch additional data in parallel
    const fetchAdditionalData = async () => {
      logger.log('Fetching additional data for:', ticker);
      const [priceHistoryData, signalsData, consensusHistoryResult, valuationData] = await Promise.allSettled([
        fetchPriceHistory(ticker, 365),
        fetchSignals(ticker),
        fetchConsensusHistory(ticker),
        fetchConsensusValuation(ticker)
      ]);
      return { priceHistoryData, signalsData, consensusHistoryResult, valuationData };
    };

    // Process API response from Promise.allSettled
    const processApiResponse = (result, setter, errorMessage, defaultValue = null) => {
      if (result.status === 'fulfilled') {
        if (!isCancelled) {
          setter(result.value);
        }
      } else {
        logger.error(errorMessage, result.reason);
        if (!isCancelled) {
          setter(defaultValue);
        }
      }
    };

    // Main data fetching function
    const fetchStockData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch stock details with retry logic
        await fetchStockDetails();
        if (isCancelled) return;
        
        // Fetch all data in parallel for better performance
        const { priceHistoryData, signalsData, consensusHistoryResult, valuationData } = await fetchAdditionalData();
        if (isCancelled) return;
        
        // Process responses
        processApiResponse(priceHistoryData, setPriceHistory, `Error fetching price history for ${ticker}:`);
        processApiResponse(signalsData, setSignals, `Error fetching signals for ${ticker}:`);
        processApiResponse(consensusHistoryResult, setConsensusHistory, `Error fetching consensus history for ${ticker}:`);
        processApiResponse(valuationData, setConsensusValuation, `Error fetching consensus valuation for ${ticker}:`);
        
        // Set initial selected month to most recent (first available date)
        if (!isCancelled && consensusHistoryResult.status === 'fulfilled' && consensusHistoryResult.value) {
          const history = consensusHistoryResult.value;
          // Extract dates and find the most recent month
          const dateSet = new Set();
          if (history.ratings && history.ratings.length > 0) {
            history.ratings.forEach(r => {
              if (r.fetched_at) {
                const date = new Date(r.fetched_at);
                const key = `${date.getFullYear()}-${date.getMonth()}`;
                dateSet.add(key);
              }
            });
          }
          if (history.price_targets && history.price_targets.length > 0) {
            history.price_targets.forEach(t => {
              if (t.fetched_at) {
                const date = new Date(t.fetched_at);
                const key = `${date.getFullYear()}-${date.getMonth()}`;
                dateSet.add(key);
              }
            });
          }
          if (dateSet.size > 0) {
            // Get most recent date (first in sorted order)
            const sortedDates = Array.from(dateSet).sort((a, b) => {
              const [yearA, monthA] = a.split('-').map(Number);
              const [yearB, monthB] = b.split('-').map(Number);
              if (yearB !== yearA) return yearB - yearA;
              return monthB - monthA;
            });
            const [year, monthIndex] = sortedDates[0].split('-').map(Number);
            setSelectedConsensusMonth({ year, month: monthIndex + 1 });
          }
        }
        
        if (!isCancelled) {
          logger.log(`Price history: ${priceHistoryData.status === 'fulfilled' ? priceHistoryData.value?.data_points?.length || 0 : 0}, Signals: ${signalsData.status === 'fulfilled' ? 'received' : 'failed'}`);
          setLoading(false);
        }
      } catch (err) {
        if (!isCancelled) {
          if (err.name === 'AbortError') {
            logger.log('Request cancelled for:', ticker);
            return;
          }
          setError('Failed to load stock data: ' + err.message);
          setLoading(false);
        }
      }
    };

    if (ticker) {
      fetchStockData();
    }

    // Cleanup function to cancel requests when component unmounts or ticker changes
    return () => {
      isCancelled = true;
      abortController.abort();
    };
  }, [ticker]);

  // Helper function to determine CSS class based on consensus score
  const getConsensusScoreClass = (score) => {
    if (score === null || score === undefined) return 'fairly-priced';
    if (score > 0.40) return 'very-undervalued';
    if (score > 0.15) return 'undervalued';
    if (score > -0.15) return 'fairly-priced';
    if (score > -0.40) return 'overvalued';
    return 'very-overvalued';
  };

  if (loading) {
    return (
      <div className="stock-detail">
        <LoadingState message="Loading stock details..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="stock-detail">
        <ErrorState message={error} />
      </div>
    );
  }

  if (!stock) {
    return (
      <div className="stock-detail">
        <ErrorState message="Stock not found" />
      </div>
    );
  }

  return (
    <div className="stock-detail">
      <div className="stock-detail-header">
        <h1>{stock.name}</h1>
      </div>

      <div className="stock-overview">
        <div className="stock-main-info">
          <div className="stock-price-section">
            {stock.current_price ? (
              <div className="current-price">
                <span className="price">${stock.current_price.toFixed(2)}</span>
                <span className="last-updated">
                  Updated {new Date(stock.last_updated).toLocaleDateString()}
                </span>
              </div>
            ) : (
              <div className="no-price">No price data available</div>
            )}
          </div>
          
          <div className="stock-details-grid">
            {stock.sector && (
              <div className="detail-item">
                <span className="label">Sector:</span>
                <span className="value">{stock.sector}</span>
              </div>
            )}
            {consensusValuation && consensusValuation.valuation_score !== null && consensusValuation.valuation_score !== undefined ? (
              <div className="detail-item">
                <span className="label">Consensus Score:</span>
                <span className={`value consensus-score consensus-score-${getConsensusScoreClass(consensusValuation.valuation_score)}`}>
                  {consensusValuation.valuation_score.toFixed(3)}
                </span>
              </div>
            ) : null}
            {signals && signals.recommendation ? (
              <div className="detail-item">
                <span className="label">Recommendation:</span>
                <span className={`value recommendation recommendation-${signals.recommendation.toLowerCase().replace('_', '-')}`}>
                  {signals.recommendation.replace('_', ' ')}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="stock-chart-section">
        {priceHistory && priceHistory.data_points && priceHistory.data_points.length > 0 ? (
          <div 
            ref={chartContainerRef} 
            className="chart-container"
            style={{ width: '100%', height: '400px' }}
          />
        ) : (
          <div className="no-chart-data">No price history data available</div>
        )}
      </div>

      <ConsensusDisplay 
        consensusHistory={consensusHistory}
        selectedMonth={selectedConsensusMonth}
        onMonthChange={setSelectedConsensusMonth}
      />
    </div>
  );
}

export default StockDetail;
